<script lang="ts">
  import { xhr, toast } from "$app/utils"
  import ImageCropper from "$app/components/ImageCropper.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Button from "$app/components/ButtonSpinner.svelte"
  import { X } from "lucide-svelte"

  let {
    plan_id,
    onclose,
    oncreated
  }: { plan_id: number; onclose: () => void; oncreated: () => void } = $props()

  let errors: Record<string, string> = $state({})

  import userImgJPG from "@images/users/default.jpg"

  let name = $state("")
  let email = $state("")

  let avatar: any = $state()
  let avatarTmp: any = $state()
  let fileInput = $state<HTMLInputElement | undefined>()
  let fileImage: any = $state()
  let avatarChanged: boolean = $state(false)
  let openImgCropper = $state(false)

  const imgClass =
    "w-full h-full bg-gray-300 rounded-full shadow-lg flex-shrink-0"

  function syncAvatar(): void {
    const imgAvatar: HTMLImageElement = document.querySelector(
      ".main-nav .user-avatar"
    )
    if (imgAvatar) {
      imgAvatar.src = avatar
    }
  }

  const onFileSelected = (e) => {
    const target = e.target as HTMLInputElement
    fileImage = target.files[0]
    let reader = new FileReader()
    reader.readAsDataURL(fileImage)
    reader.onload = (e) => {
      avatarTmp = e.target.result

      openImgCropper = true
      avatarChanged = true
      syncAvatar()
    }
  }

  // Send Recognition
  let savingExternalRecipient = $state(false)
  async function saveExternalRecipient(): Promise<void> {
    savingExternalRecipient = true

    let formData = new FormData()
    formData.append("external_recipient[name]", name)
    formData.append("external_recipient[email]", email)
    if (fileImage) {
      formData.append("external_recipient[avatar]", fileImage)
    }

    try {
      const resp = await xhr.post(
        `/plans/${plan_id}/external_recipients.json`,
        formData
      )
      oncreated()
    } catch (e) {
      Honeybadger.notify(e)
      errors = e.response.data.errors
      toast("There was an error adding the external recipient", "error")
    } finally {
      savingExternalRecipient = false
    }
  }
</script>

<div
  class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16"
>
  <div class="pointer-events-auto w-screen max-w-lg">
    <div class="flex h-full flex-col bg-white shadow-xl">
      <div class="flex-1">
        <!-- Header -->
        <div class="bg-gray-50 px-4 py-6 sm:px-6">
          <div class="flex items-start justify-between space-x-3">
            <div class="space-y-1">
              <h2
                class="text-lg font-medium text-gray-900"
                id="slide-over-title"
              >
                Add External Recipient
              </h2>
              <p class="text-sm text-gray-500">
                An external recipient is someone who is not a member of your
                plan that you would like to send a recognition to.
              </p>
            </div>
            <div class="flex h-7 items-center">
              <button
                type="button"
                class="text-gray-400 hover:text-gray-500"
                onclick={() => onclose()}
              >
                <span class="sr-only">Close panel</span>
                <X class="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <div
          class="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0"
        >
          <div
            class="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5"
          >
            <div class="flex items-center">
              <label
                for="name"
                class="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                >Full name</label
              >
            </div>
            <div
              class="sm:col-span-2 input-cont flex items-center"
              class:error={errors.name}
            >
              <input
                type="text"
                id="name"
                bind:value={name}
                class="block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
              />
              <div class="msg">{errors.name}</div>
            </div>
          </div>
          <div
            class="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5"
          >
            <div class="flex items-center">
              <label
                for="email"
                class="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                >Email</label
              >
            </div>
            <div
              class="sm:col-span-2 input-cont flex items-center"
              class:error={errors.email}
            >
              <input
                type="email"
                id="email"
                bind:value={email}
                class="block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
              />
              <div class="msg">{errors.email}</div>
            </div>
          </div>

          <div
            class="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5"
          >
            <div>
              <label
                for="cover-photo"
                class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                >Photo</label
              >
            </div>
            <div class="mt-1 sm:col-span-2 sm:mt-0">
              <div class="w-full flex justify-start">
                <div class="h-20 w-20 rounded-full bg-gray-100">
                  {#if avatar}
                    <img
                      src={avatar}
                      id="user-avatar"
                      alt="User avatar"
                      class={imgClass}
                      data-name="avatar-image"
                    />
                  {:else}
                    <img
                      src={userImgJPG}
                      alt="Select Avatar"
                      class={imgClass}
                      data-name="avatar-image"
                    />
                  {/if}
                </div>
                <div class="text-sm text-black text-left pt-2 ml-4">
                  <button
                    type="button"
                    class="p-1 border border-gray-300 relative cursor-pointer bg-white rounded-lg text-center font-medium text-black hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    onclick={() => {
                      fileInput.click()
                    }}
                  >
                    <span>Upload Picture</span>
                  </button>
                  <p class="text-xs text-gray-500 mt-2 ml-2">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
                {#if openImgCropper}
                  <div
                    class="absolute top-14 right-20 bg-gray-50 shadow-lg py-2 px-4 border border-gray-200"
                  >
                    <ImageCropper
                      image={avatarTmp}
                      open={openImgCropper}
                      file={fileImage}
                      cropShape="round"
                      modal={false}
                      cropperClose={() => {
                        openImgCropper = false
                      }}
                      imageCropped={(data) => {
                        console.log(data)
                        avatar = data.image
                        fileImage = data.file

                        //toast("Update your settings to save your picture", "")
                      }}
                    />
                  </div>
                {/if}
              </div>
              <input
                type="file"
                bind:this={fileInput}
                class="hidden"
                onchange={onFileSelected}
                accept=".jpg, .jpeg, .png"
              />
            </div>
          </div>
        </div>
      </div>
      <!-- Action buttons -->
      <div class="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            onclick={() => onclose()}
            class="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >Cancel</button
          >
          <Button
            class="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-core shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            processing={savingExternalRecipient}
            onclick={() => {
              saveExternalRecipient()
            }}
          >
            Save External Recipient
          </Button>
        </div>
      </div>
    </div>
  </div>
</div>
